import { fetchWithRefresh } from "../utils/fetchWithRefresh";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const usersRequest = {
    async register({ email, password, firstName, lastName, phone, acceptedTermsVersion}) {
        const res = await fetch(`${API_BASE}/users/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password, firstName, lastName, phone, acceptedTermsVersion }),
        });

        if (!res.ok) throw new Error("Failed to register");
        return res.json();
    },

    async staffRegister({ email, password, firstName, lastName, phoneNumber, shopId }) {
        const res = await fetch(`${API_BASE}/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password, firstName, lastName, phoneNumber, shopId }),
        });

        if (!res.ok) throw new Error("Failed to register");
        return res.json();
    },

    async confirmationCode({ email, code }) {
        const res = await fetch(`${API_BASE}/confirm`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, code }),
        });

        if (!res.ok) throw new Error("Failed to confirm");
        return res.json();
    },

    async login({ email, password }) {
        const res = await fetch(`${API_BASE}/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });
    
        if (!res.ok) {
            const data = await res.json();
            const error = new Error(data.error || "Failed to login");
            error.details = data.details;
            throw error;
        }
    
        return res.json();
    },

    async forgotPassword(email) {
        const res = await fetch(`${API_BASE}/forgot-password`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
        });

        // Always return JSON (backend intentionally hides existence)
        return res.json();
    },

    async confirmForgotPassword({ email, code, newPassword }) {
        const res = await fetch(`${API_BASE}/confirm-forgot-password`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email,
                code,
                newPassword,
            }),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || "Could not reset password");
        }

        return res.json();
    },

    async removeSelf(accessToken) {
        const res = await fetch(`${API_BASE}/me`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    
        if (!res.ok) throw new Error("Failed to delete account");
        return res.json();
    },

    async update(userId, updates) {
        const res = await fetch(`${API_BASE}/users/${userId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updates),
        });

        if (!res.ok) throw new Error("Failed to update user");
        return res.json();
    },

    async remove(userId) {
        const res = await fetch(`${API_BASE}/users/${userId}`, {
            method: "DELETE",
        });

        if (!res.ok) throw new Error("Failed to delete user");
        return res.json();
    },

    async book({ email, password }) {
        const res = await fetch(`${API_BASE}/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });

        if (!res.ok) throw new Error("Failed to login");
        return res.json(); // contains tokens from backend
    },

    async getUserBookings(userId, accessToken) {
        const res = await fetch(`${API_BASE}/users/${userId}/bookings`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`, // 🔑 verify JWT on backend
            },
        });

        if (!res.ok) throw new Error("Failed to fetch bookings");
        return res.json();
    },

    async getBooking(bookingId) {
        const res = await fetch(`${API_BASE}/bookings/${bookingId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!res.ok) throw new Error("Failed to fetch bookings");
        return res.json();
    },

    async me(accessToken) {
        if (!accessToken) throw new Error("ID token missing");
        const res = await fetch(`${API_BASE}/me`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (!res.ok) throw new Error(`Failed to fetch current user: ${res.status}`);
        return res.json();
    },

    async createReview(bookingId, rating, comment) {
        const res = await fetch(`${API_BASE}/reviews`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bookingId, rating, comment }),
        });

        if (!res.ok) throw new Error("Failed to submit review");
        return res.json();
    },

    async updateReview(reviewId, { rating, comment }) {
        const res = await fetch(`${API_BASE}/reviews/${reviewId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                rating,
                comment,
            }),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || "Failed to update review");
        }

        return res.json();
    },

    async flagReview(shopId, reviewId, { reason, message, email }) {
        const res = await fetch(
            `${API_BASE}/reviews/${shopId}/${reviewId}/flag`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    reason,
                    message,
                    email,
                }),
            }
        );
    
        if (!res.ok) throw new Error("Failed to flag review");
        return res.json();
    },

    async cancelBookingCustomer(bookingId) {
        const res = await fetch(`${API_BASE}/bookings/${bookingId}/cancel`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!res.ok) throw new Error(await res.text());
        return res.json(); // { message: "Booking cancelled successfully." }
    },
    async getNotificationPreferences(accessToken) {
        const res = await fetch(`${API_BASE}/get/users/notifications`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || "Failed to fetch notification preferences");
        }

        return res.json();
    },

    async updateNotificationPreferences(payload, accessToken) {
        const res = await fetch(`${API_BASE}/users/notifications`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload), // ✅ pass-through
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || "Failed to update notification preferences");
        }

        return res.json();
    },
};

export default usersRequest;
